import axios from 'axios';
import { API_V1_URL } from '../../../config';

const api = axios.create({
  baseURL: API_V1_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors (token expired — but NOT during login/register/partner)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || '';
    const isAuthEndpoint =
      url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/partner/');
    if (error.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  register: (userData) => api.post('/auth/register', userData),
  login: (credentials) => api.post('/auth/login', credentials),
  partnerTwingpAuth: (payload) => api.post('/auth/partner/twingp', payload),
  getCurrentUser: () => api.get('/auth/me'),
  updateProfile: (data) => api.patch('/auth/me', data),
  verifyEmail: (email, otp) => api.post('/auth/verify-email', { email, otp }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  verifyResetOtp: (email, otp) => api.post('/auth/verify-reset-otp', { email, otp }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
};

// Submissions API
export const submissionsAPI = {
  create: (submissionData) => api.post('/submissions', submissionData),
  getAll: (params) => api.get('/submissions', { params }),
  getById: (id) => api.get(`/submissions/${id}`),
};

// Wallet API
export const walletAPI = {
  getBalance: () => api.get('/wallet/balance'),
  getTransactions: (params) => api.get('/wallet/transactions', { params }),
  getPacks: () => api.get('/wallet/packs'),
  purchasePack: (data) => api.post('/wallet/purchase', data),
  verifyPayment: (data) => api.post('/wallet/verify', data),
};

// Payment status check (reconciliation)
export const paymentAPI = {
  checkStatus: (razorpay_order_id) => api.post('/payments/check-status', { razorpay_order_id }),
};

// Projects API (public listing)
export const projectsAPI = {
  list: (params) => api.get('/projects', { params }),
  getById: (id) => api.get(`/projects/${id}`),
};

// Internship API (requires active internship module)
export const internshipAPI = {
  enroll:    (projectId, selectedTechStack) => api.post(`/internship/enroll/${projectId}`, { selected_tech_stack: selectedTechStack }),
  getMyProject: ()       => api.get('/internship/my-project'),
  getStage:  (stageId)   => api.get(`/internship/stages/${stageId}`),
  submitStage: (stageId, data) => api.post(`/internship/stages/${stageId}/submit`, data),
  getCertificate: ()     => api.get('/internship/certificate'),
  downloadCertificatePDF: () => api.get('/internship/certificate/download', { responseType: 'blob' }),
  // Razorpay payment — internship activation
  createOrder:   ()      => api.post('/internship/create-order'),
  verifyPayment: (data)  => api.post('/internship/verify-payment', data),
};

// Admin — Orgs API
export const adminOrgsAPI = {
  create: (data) => api.post('/admin/orgs', data),
  list: () => api.get('/admin/orgs'),
  getById: (id) => api.get(`/admin/orgs/${id}`),
  update: (id, data) => api.patch(`/admin/orgs/${id}`, data),
  generateCodes: (id, data) => api.post(`/admin/orgs/${id}/codes`, data),
  listCodes: (id, params) => api.get(`/admin/orgs/${id}/codes`, { params }),
  deleteCode: (orgId, codeId) => api.delete(`/admin/orgs/${orgId}/codes/${codeId}`),
  addCredits: (id, data) => api.post(`/admin/orgs/${id}/credits`, data),
  getUsage: (id) => api.get(`/admin/orgs/${id}/usage`),
};

// Admin — Projects API
export const adminProjectsAPI = {
  create: (data) => api.post('/admin/projects', data),
  update: (id, data) => api.patch(`/admin/projects/${id}`, data),
  deactivate: (id) => api.delete(`/admin/projects/${id}`),
  addStage: (projectId, data) => api.post(`/admin/projects/${projectId}/stages`, data),
  updateStage: (stageId, data) => api.patch(`/admin/stages/${stageId}`, data),
  deleteStage: (stageId) => api.delete(`/admin/stages/${stageId}`),
  getPendingReviews: (params) => api.get('/admin/stage-reviews', { params }),
  reviewSubmission: (id, data) => api.patch(`/admin/stage-reviews/${id}`, data),
};

// Admin — Stats API
export const adminStatsAPI = {
  getLLMUsage: () => api.get('/admin/stats/llm-usage'),
  getUsers: (params) => api.get('/admin/users', { params }),
  getSubmissions: (params) => api.get('/admin/submissions', { params }),
};

// Legacy submission API (without auth)
export const legacyAPI = {
  submit: (submissionData) => api.post('/submit', submissionData),
};

// Mentor Support API
export const supportAPI = {
  contact: (data) => {
    const formData = new FormData();
    formData.append('subject', data.subject);
    formData.append('message', data.message);
    if (data.attachment) {
      formData.append('attachment', data.attachment);
    }
    return api.post('/support/contact', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export default api;

